import React, { useState } from 'react';
import validator from 'validator';

import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from './BasicMaterial.jsx';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import PhoneField from './PhoneField.jsx';

const ContactInformationGroup = ({
	name,
	label,
	value = {},
	onChange,
	required = false,
	disabled = false,
	invalidEmailText = 'Please enter a valid email address',
	nameLabel = 'Name',
	emailLabel = 'Email',
	phoneLabel = 'Phone',
	namePlaceholder = 'Enter full name',
	emailPlaceholder = 'Enter email address',
	...props
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	// Use value directly from props - fully controlled component
	const currentValue = {
		name: value?.name || '',
		email: value?.email || '',
		phone: value?.phone || '',
		type: label,
	};

	const isValidEmail = (email) => {
		if (!email || email.trim() === '') return false;
		return validator.isEmail(email);
	};

	const isCompleted = () => {
		if (!currentValue.name) return false;
		if (currentValue.email && currentValue.email.trim() !== '' && !isValidEmail(currentValue.email)) return false;
		return currentValue.phone && currentValue.phone.trim() !== '';
	};

	const handleFieldChange = (fieldName, fieldValue) => {
		const newValue = { ...currentValue, [fieldName]: fieldValue };
		onChange({ target: { name, value: newValue } });
	};

	const toggleExpanded = () => {
		setIsExpanded(!isExpanded);
	};

	const completed = isCompleted();

	return (
		<Paper
			variant="outlined"
			sx={{
				borderRadius: 2,
				border: '1px solid',
				borderColor: completed ? 'success.main' : 'grey.300',
				marginBottom: 2,
				overflow: 'hidden',
				...props.sx,
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					padding: 2,
					cursor: disabled ? 'default' : 'pointer',
				}}
				onClick={disabled ? undefined : toggleExpanded}
			>
				{/* Status Icon */}
				<Box sx={{ marginRight: 2, display: 'flex', alignItems: 'center' }}>
					{completed ? (
						<CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
					) : (
						<EditIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
					)}
				</Box>

				{/* Label */}
				<Typography
					variant="body1"
					sx={{
						flexGrow: 1,
						fontWeight: 500,
						color: completed ? 'success.main' : 'text.primary',
					}}
				>
					{label}
				</Typography>

				{/* Required Badge */}
				{required && (
					<Chip
						label="Required"
						size="small"
						sx={{
							backgroundColor: 'error.main',
							color: 'white',
							fontSize: '0.75rem',
							height: 20,
							marginRight: 1,
						}}
					/>
				)}

				{/* Expand/Collapse Icon */}
				<IconButton
					size="small"
					disabled={disabled}
					sx={{
						transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
						transition: 'transform 0.2s ease-in-out',
					}}
				>
					<ExpandMoreIcon />
				</IconButton>
			</Box>

			{/* Expandable Content */}
			<Collapse in={isExpanded}>
				<Box sx={{ padding: 2, paddingTop: 0 }}>
					<TextField
						fullWidth
						required
						label={nameLabel}
						value={currentValue.name}
						onChange={(e) => handleFieldChange('name', e.target.value)}
						disabled={disabled}
						placeholder={namePlaceholder}
					/>
					<TextField
						key="Email"
						name="email"
						label={emailLabel}
						type="email"
						value={currentValue.email}
						onChange={(e) => handleFieldChange('email', e.target.value)}
						disabled={disabled}
						sx={{ marginTop: 2, marginBottom: 2 }}
						placeholder={emailPlaceholder}
						error={currentValue.email.trim() !== '' && !isValidEmail(currentValue.email)}
						helperText={
							currentValue.email.trim() !== '' && !isValidEmail(currentValue.email)
								? invalidEmailText
								: ' '
						}
						FormHelperTextProps={{
							sx: { minHeight: '20px' },
						}}
					/>
					<PhoneField
						name="phone"
						required
						label={phoneLabel}
						value={currentValue.phone}
						onChange={(e) => handleFieldChange('phone', e.target.value)}
						disabled={disabled}
						allowFixedLine={true}
						defaultCountry="BE"
					/>
				</Box>
			</Collapse>
		</Paper>
	);
};

ContactInformationGroup.type = 'ContactInformationGroup';

ContactInformationGroup.spec = [{
	name: 'ContactInformationGroup',
	key: 'ContactInformationGroup',
	title: 'Contact Information Group',
	icon: 'User',
	category: 'input',
	translatable: ['label', 'nameLabel', 'emailLabel', 'phoneLabel', 'namePlaceholder', 'emailPlaceholder', 'invalidEmailText'],
	defaultProperties: {
		nameLabel: 'Name',
		emailLabel: 'Email',
		phoneLabel: 'Phone',
		namePlaceholder: 'Enter full name',
		emailPlaceholder: 'Enter email address',
		invalidEmailText: 'Please enter a valid email address',
	},
	fields: [{
		type: 'TextField',
		label: 'Label',
		name: 'label',
		helperText: 'The main label for this contact group (e.g., "Primary Contact")',
	}, {
		type: 'Checkbox',
		label: 'Required?',
		name: 'required',
	}, {
		type: 'TextField',
		label: 'Name Field Label',
		name: 'nameLabel',
	}, {
		type: 'TextField',
		label: 'Email Field Label',
		name: 'emailLabel',
	}, {
		type: 'TextField',
		label: 'Phone Field Label',
		name: 'phoneLabel',
	}, {
		type: 'TextField',
		label: 'Name Placeholder',
		name: 'namePlaceholder',
	}, {
		type: 'TextField',
		label: 'Email Placeholder',
		name: 'emailPlaceholder',
	}, {
		type: 'TextField',
		label: 'Invalid Email Error Message',
		name: 'invalidEmailText',
	}],
}];

export default ContactInformationGroup;
